import React from 'react';
import cx from 'classnames';
import BucketTile from './BucketTile';
import { LOG, zoneCapacity, isZone, isLiteral } from './bucketingConstants';
import { squareGridColumns, squareGridRowCount } from '../Formative/squareGrid';

const PLACED_GRID_MIN_TRACKS = 2;

export function placedGrid(capacity, placedCount) {
  if (capacity <= 1) return { columnCount: 1, rowCount: 1 };
  return {
    columnCount: Math.max(PLACED_GRID_MIN_TRACKS, squareGridColumns(placedCount)),
    rowCount: Math.max(PLACED_GRID_MIN_TRACKS, squareGridRowCount(placedCount)),
  };
}

function BucketLabel({ bucket }) {
  if (bucket.imageUrl) {
    return (
      <span className="bucketing__bucketLabel bucketing__bucketLabel--image">
        <img src={bucket.imageUrl} alt={bucket.text || ''} />
      </span>
    );
  }
  if (!bucket.text) return null;
  return <span className="bucketing__bucketLabel bucketing__bucketLabel--text">{bucket.text}</span>;
}

function Literal({ option }) {
  if (option.imageUrl) {
    return (
      <img className="bucketing__literalImage" src={option.imageUrl} alt={option.text || ''} />
    );
  }
  return <span className="bucketing__literalText">{option.text}</span>;
}

function Bucket({
  bucket,
  contents,
  poolByKey,
  isTarget,
  pointed,
  disabled,
  onBucketClick,
  onPlacedItemClick,
}) {
  const capacity = zoneCapacity(bucket);
  const isFull = contents.length >= capacity;
  const { columnCount, rowCount } = placedGrid(capacity, contents.length);

  return (
    <div
      className={cx('bucketing__bucket', 'pointerTarget', 'bottom', {
        'bucketing__bucket--target': isTarget,
        'bucketing__bucket--full': isFull,
        'bucketing__bucket--occupied': contents.length > 0,
        showPointer: pointed,
      })}
      role="group"
      aria-label={bucket.text || bucket.key}
    >
      <button
        type="button"
        className="bucketing__bucketDropZone"
        onClick={() => onBucketClick(bucket)}
        disabled={disabled}
        aria-label={bucket.text || bucket.key}
      />
      <BucketLabel bucket={bucket} />
      <div
        className="bucketing__bucketContents"
        style={{
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))`,
        }}
      >
        {contents.map((poolKey) => {
          const poolItem = poolByKey[poolKey];
          if (!poolItem) return null;
          return (
            <BucketTile
              key={poolKey}
              option={poolItem}
              placed
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                onPlacedItemClick(bucket, poolKey);
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function BucketingBoard({
  rows,
  placements,
  poolByKey,
  selectedPoolKey,
  hideBoard,
  pointerTarget,
  disabled,
  onBucketClick,
  onPlacedItemClick,
}) {
  if (rows.length === 0) {
    LOG('board has no drop zones — item.dropZones is empty or missing');
    return <div className={cx('bucketing__board', 'bucketing__board--empty', { hidden: hideBoard })} />;
  }

  return (
    <div className={cx('bucketing__board', { hidden: hideBoard })}>
      {rows.map((row, rowIndex) => (
        <div className="bucketing__boardRow" key={`row-${rowIndex}`}>
          {row.map((option, entryIndex) => {
            if (isZone(option)) {
              return (
                <Bucket
                  key={option.key}
                  bucket={option}
                  contents={placements[option.key] || []}
                  poolByKey={poolByKey}
                  isTarget={!!selectedPoolKey}
                  pointed={pointerTarget === option.key}
                  disabled={disabled}
                  onBucketClick={onBucketClick}
                  onPlacedItemClick={onPlacedItemClick}
                />
              );
            }
            if (isLiteral(option)) {
              return <Literal key={`literal-${rowIndex}-${entryIndex}`} option={option} />;
            }
            LOG(`board row ${rowIndex} entry ${entryIndex} has no renderer for kind "${option?.kind}" — dropped`);
            return null;
          })}
        </div>
      ))}
    </div>
  );
}
